/**
 * 3Dの現在地を、間取り図（画像）の上の位置に変換する。
 *
 * 3Dの座標と間取り図の座標は、向き・左右反転・縮尺がばらばらなので、
 * 「視点の3D座標」と「その視点に置いた図面の印」の対応から、両者をつなぐ変換を求める。
 *
 *   図面X = a・3Dx + b・3Dz + c
 *   図面Y = d・3Dx + e・3Dz + f
 *
 * この形（アフィン変換）なら、回転・反転・縮尺・ずれをまとめて表せる。
 * 対応が3組以上あれば最小二乗法で係数が決まり、印の位置が多少ずれていても全体として最も合う変換になる。
 *
 * 以前は「全部の印を距離で重み付けした平均」を使っていたが、
 * それだと部屋の端にいるほど中央（や反対側）へ引っ張られ、現在地が逆に出ることがあった。
 */

import { WALK } from '../config.js';

/** 一直線に並んだ対応しか無いなど、変換が決まらないときの判定に使う */
const MIN_DETERMINANT = 1e-9;
/** 図面からこれ以上はみ出したら、現在地は出さない（対応が合っていないとき誤った場所を出さないため） */
const OUTSIDE_MARGIN = 0.25;
/** 視点の高さがこれ以上離れていたら別の階とみなす(m) */
const LEVEL_GAP = 0.8;
/**
 * 変換が印をこの程度（図面の幅に対する割合）より再現できないなら、信用しない。
 * 手で置いた印がばらばらな物件で、でたらめな現在地を出さないための歯止め。
 */
const MAX_FIT_ERROR = 0.07;

/**
 * 視点と印の対応から変換を作る。作れなければ null。
 * @param {{name: string, position: number[]}[]} viewpoints
 * @param {Map<string, {x: number, y: number}>} marks
 */
export function createFloorplanProjection(viewpoints, marks) {
  const anchors = (viewpoints ?? [])
    .map((viewpoint) => ({ viewpoint, mark: marks?.get(viewpoint.name) }))
    .filter(({ viewpoint, mark }) => Array.isArray(viewpoint.position) && mark !== undefined)
    .map(({ viewpoint, mark }) => ({
      name: viewpoint.name,
      wx: viewpoint.position[0],
      // 視点は目線の高さなので、床の高さに直してから階を比べる
      wy: viewpoint.position[1] - WALK.viewpointEyeHeight,
      wz: viewpoint.position[2],
      px: mark.x,
      py: mark.y,
    }));
  if (anchors.length === 0) return null;

  /*
   * 階ごとに分けて変換を作る。
   * ロフトや中二階は、間取り図では別枠の小さな図として描かれることが多く、
   * 1階と同じ変換では表せないため。視点の高さが LEVEL_GAP 以上離れていたら別の階とみなす。
   */
  const levels = groupByLevel(anchors).map((group) => ({
    height: group.reduce((sum, a) => sum + a.wy, 0) / group.length,
    anchors: group,
    ...fitWithError(group),
  }));

  /** 今いる高さに一番近い階の変換を選ぶ */
  const levelAt = (y) =>
    levels.reduce((best, level) => (Math.abs(level.height - y) < Math.abs(best.height - y) ? level : best));

  return {
    anchors,
    levels,
    /**
     * 3Dの (x, z) を図面の (x, y) にする。
     * 変換が信用できないとき（印の付け方がばらばら等）は、無理に補間せず一番近い印に寄せる。
     */
    project(x, z, y = 0) {
      const level = levelAt(y);
      if (level.solve !== null && level.error <= MAX_FIT_ERROR) return level.solve(x, z);
      const near = nearest(level.anchors, x, z);
      return { x: near.px, y: near.py, snapped: true };
    },
    /** 一番近い視点の名前（部屋名の表示に使う） */
    roomAt(x, z, y = 0) {
      return nearest(levelAt(y).anchors, x, z).name;
    },
    /** その高さで、なめらかな現在地が出せるか（印がそろっているか） */
    exactAt(y = 0) {
      const level = levelAt(y);
      return level.solve !== null && level.error <= MAX_FIT_ERROR;
    },
  };
}

/** 視点の高さで階ごとにまとめる */
function groupByLevel(anchors) {
  const sorted = [...anchors].sort((a, b) => a.wy - b.wy);
  const groups = [[sorted[0]]];
  for (const anchor of sorted.slice(1)) {
    const current = groups[groups.length - 1];
    if (anchor.wy - current[current.length - 1].wy > LEVEL_GAP) groups.push([anchor]);
    else current.push(anchor);
  }
  return groups;
}

/** 変換と、その変換が印をどれだけ再現できているか（図面の幅に対する平均のずれ） */
function fitWithError(anchors) {
  const solve = fitAffine(anchors);
  if (solve === null) return { solve: null, error: Infinity };
  let total = 0;
  for (const a of anchors) {
    const p = solve(a.wx, a.wz);
    total += Math.hypot(p.x - a.px, p.y - a.py);
  }
  return { solve, error: total / anchors.length };
}

/**
 * 現在地と向きを図面の座標に直す。対応が無ければ null。
 * @param {{x: number, z: number}} position 3Dの現在地
 * @param {{x: number, z: number}} forward  向いている方向
 */
export function locateOnFloorplan(position, forward, viewpoints, marks) {
  const projection = createFloorplanProjection(viewpoints, marks);
  if (projection === null) return null;

  const point = projection.project(position.x, position.z, position.y ?? 0);
  // 図面から大きく外れる＝対応が合っていない。誤った現在地を出すより、出さない方がよい
  if (
    point.x < -OUTSIDE_MARGIN ||
    point.x > 1 + OUTSIDE_MARGIN ||
    point.y < -OUTSIDE_MARGIN ||
    point.y > 1 + OUTSIDE_MARGIN
  ) {
    return null;
  }

  // 少し前方の点も同じ変換に通して、図面上での向きを出す
  const y = position.y ?? 0;
  const ahead = projection.project(position.x + forward.x * 0.8, position.z + forward.z * 0.8, y);
  const dx = ahead.x - point.x;
  const dy = ahead.y - point.y;
  // 図面は上が -y。上向きを0度として時計回りに測る
  const angle = Math.abs(dx) + Math.abs(dy) < 1e-9 ? 0 : (Math.atan2(dx, -dy) * 180) / Math.PI;

  return {
    x: clamp01(point.x),
    y: clamp01(point.y),
    angle,
    room: projection.roomAt(position.x, position.z, y),
    /** true = 印がそろっておらず、一番近い印に寄せているだけ（細かい位置は当てにならない） */
    approximate: point.snapped === true,
  };
}

/**
 * 最小二乗法でアフィン変換の係数を求める。
 * 3組未満、または対応が一直線に並んでいるときは null（変換が決まらない）。
 */
function fitAffine(anchors) {
  if (anchors.length < 3) return null;

  // 正規方程式 (AᵀA)・係数 = Aᵀ・目標 を作る。A の各行は [3Dx, 3Dz, 1]
  let sxx = 0;
  let sxz = 0;
  let sx = 0;
  let szz = 0;
  let sz = 0;
  let n = 0;
  for (const a of anchors) {
    sxx += a.wx * a.wx;
    sxz += a.wx * a.wz;
    sx += a.wx;
    szz += a.wz * a.wz;
    sz += a.wz;
    n += 1;
  }
  const m = [
    [sxx, sxz, sx],
    [sxz, szz, sz],
    [sx, sz, n],
  ];
  const inverse = invert3(m);
  if (inverse === null) return null;

  const coefficients = (pick) => {
    const rhs = [0, 0, 0];
    for (const a of anchors) {
      const v = pick(a);
      rhs[0] += a.wx * v;
      rhs[1] += a.wz * v;
      rhs[2] += v;
    }
    return [0, 1, 2].map((i) => inverse[i][0] * rhs[0] + inverse[i][1] * rhs[1] + inverse[i][2] * rhs[2]);
  };

  const cx = coefficients((a) => a.px);
  const cy = coefficients((a) => a.py);
  return (x, z) => ({
    x: cx[0] * x + cx[1] * z + cx[2],
    y: cy[0] * x + cy[1] * z + cy[2],
  });
}

/** 3×3 の逆行列。求まらなければ null */
function invert3(m) {
  const [a, b, c] = m[0];
  const [d, e, f] = m[1];
  const [g, h, i] = m[2];
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (!Number.isFinite(det) || Math.abs(det) < MIN_DETERMINANT) return null;
  return [
    [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
    [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
    [(d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det],
  ];
}

function nearest(anchors, x, z) {
  return anchors.reduce((best, a) => ((x - a.wx) ** 2 + (z - a.wz) ** 2 < (x - best.wx) ** 2 + (z - best.wz) ** 2 ? a : best));
}

const clamp01 = (value) => Math.min(1, Math.max(0, value));
